import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useHousehold, useGenerateInvite, useRemoveMember, useRenameHousehold, useUpdateMyName } from '../hooks/useHousehold';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { LogoutIcon, ShareIcon, UserPlusIcon, CheckIcon, EditIcon, XIcon } from '../components/ui/icons';
import { useInstallBanner } from '../hooks/useInstallBanner';
import { useToast } from '../components/ui/Toast';
import { L } from '../lib/labels';

const NOTIF_KEY = 'hs_push_notifications';

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 mb-2">
      {children}
    </p>
  );
}

function SettingsRow({
  label,
  value,
  action,
  danger,
}: {
  label: string;
  value?: string;
  action?: { label: string; onClick: () => void; loading?: boolean; icon?: React.ReactNode };
  danger?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3.5 px-4 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${danger ? 'text-red-500' : 'text-gray-900'}`}>
          {label}
        </p>
        {value && <p className="text-xs text-gray-500 truncate mt-0.5">{value}</p>}
      </div>
      {action && (
        <button
          onClick={action.onClick}
          disabled={action.loading}
          className={`inline-flex items-center gap-1.5 text-sm font-semibold ml-3 flex-shrink-0 touch-manipulation ${
            danger ? 'text-red-500' : 'text-brand-600'
          }`}
        >
          {action.loading ? '...' : (
            <>
              {action.icon}
              {action.label}
            </>
          )}
        </button>
      )}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors touch-manipulation',
        checked ? 'bg-brand-500' : 'bg-gray-200',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}

export function SettingsScreen() {
  const navigate = useNavigate();
  const { user, signOut, isAdmin, email } = useAuthContext();
  const { data: household } = useHousehold();
  const { addToast } = useToast();
  const generateInvite = useGenerateInvite();
  const removeMember = useRemoveMember();
  const renameHousehold = useRenameHousehold();
  const updateMyName = useUpdateMyName();

  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);
  const [editingListName, setEditingListName] = useState(false);
  const [listNameValue, setListNameValue] = useState('');
  const [editingMyName, setEditingMyName] = useState(false);
  const [myNameValue, setMyNameValue] = useState('');
  const [pushNotifications, setPushNotifications] = useState<boolean>(() => {
    try { return localStorage.getItem(NOTIF_KEY) !== 'false'; } catch { return true; }
  });

  const isPremium = isAdmin || ['PREMIUM', 'COURTESY', 'LIFE_PARTNER'].includes(household?.plan ?? '');
  const isOwner = household?.ownerId === user?.userId;
  const { promptAvailable, install: installPwa } = useInstallBanner();

  const myMember = household?.members.find((m) => m.userId === user?.userId);

  const handleSaveListName = async () => {
    if (!listNameValue.trim()) return;
    try {
      await renameHousehold.mutateAsync(listNameValue.trim());
      setEditingListName(false);
      addToast('Nome da lista atualizado!', 'success');
    } catch {
      addToast('Erro ao renomear lista.', 'error');
    }
  };

  const handleSaveMyName = async () => {
    if (!myNameValue.trim()) return;
    try {
      await updateMyName.mutateAsync(myNameValue.trim());
      setEditingMyName(false);
      addToast('Seu nome foi atualizado!', 'success');
    } catch {
      addToast('Erro ao atualizar nome.', 'error');
    }
  };

  const handleTogglePush = (v: boolean) => {
    setPushNotifications(v);
    try { localStorage.setItem(NOTIF_KEY, String(v)); } catch { /* ignore */ }
    addToast(v ? L.errors.notifOn : L.errors.notifOff, 'info');
  };

  const handleGenerateInvite = async () => {
    try {
      const result = await generateInvite.mutateAsync();
      setInviteUrl(result.inviteUrl);
      await navigator.clipboard.writeText(result.inviteUrl);
      addToast(L.errors.inviteCopied, 'success');
    } catch {
      addToast(L.errors.inviteGenerate, 'error');
    }
  };

  const handleSignOut = () => setShowSignOutModal(true);

  const handleConfirmSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch {
      addToast(L.errors.signOut, 'error');
      setSigningOut(false);
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!removeTarget) return;
    try {
      await removeMember.mutateAsync(removeTarget.id);
      addToast(`${removeTarget.name} removido(a).`, 'success');
      setRemoveTarget(null);
    } catch {
      addToast(L.errors.removeMember, 'error');
    }
  };

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title={L.settings.title} />

      <div className="flex-1 px-4 py-5 space-y-5">

        {/* Conta */}
        <div>
          <SectionHeader>{L.settings.accountSection}</SectionHeader>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <SettingsRow
              label={L.settings.email}
              value={email || '-'}
            />
            {promptAvailable && (
              <SettingsRow
                label={L.settings.installApp}
                value={L.settings.installAppDesc}
                action={{ label: L.settings.installButton, onClick: installPwa }}
              />
            )}
            <SettingsRow
              label={L.settings.signOut}
              danger
              action={{
                label: signingOut ? L.settings.signingOut : L.settings.signOutShort,
                onClick: handleSignOut,
                loading: signingOut,
                icon: <LogoutIcon size={15} />,
              }}
            />
          </div>
        </div>

        {/* Lista / Família */}
        {household && (
          <div>
            <SectionHeader>{L.settings.householdSection}</SectionHeader>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* List name — editable by owner */}
              {editingListName && isOwner ? (
                <div className="flex items-center gap-2 py-3 px-4 border-b border-gray-100">
                  <input
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 min-w-0 focus:outline-none focus:border-brand-400"
                    value={listNameValue}
                    onChange={(e) => setListNameValue(e.target.value)}
                    maxLength={100}
                    autoFocus
                  />
                  <button
                    onClick={handleSaveListName}
                    disabled={renameHousehold.isPending}
                    aria-label="Salvar"
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-brand-600 hover:bg-brand-50 touch-manipulation flex-shrink-0 disabled:opacity-50"
                  >
                    <CheckIcon size={16} />
                  </button>
                  <button
                    onClick={() => setEditingListName(false)}
                    aria-label="Cancelar"
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 touch-manipulation flex-shrink-0"
                  >
                    <XIcon size={16} />
                  </button>
                </div>
              ) : (
                <SettingsRow
                  label={L.settings.listName}
                  value={household.name}
                  action={isOwner ? { label: 'Editar', onClick: () => { setListNameValue(household.name); setEditingListName(true); }, icon: <EditIcon size={14} /> } : undefined}
                />
              )}

              {household.members.map((member) => {
                const isMe = member.userId === user?.userId;
                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between py-3 px-4 border-b border-gray-100 last:border-0"
                  >
                    {editingMyName && isMe ? (
                      <>
                        <input
                          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 min-w-0 focus:outline-none focus:border-brand-400 mr-2"
                          value={myNameValue}
                          onChange={(e) => setMyNameValue(e.target.value)}
                          maxLength={100}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveMyName}
                          disabled={updateMyName.isPending}
                          aria-label="Salvar"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-brand-600 hover:bg-brand-50 touch-manipulation flex-shrink-0 mr-1 disabled:opacity-50"
                        >
                          <CheckIcon size={16} />
                        </button>
                        <button
                          onClick={() => setEditingMyName(false)}
                          aria-label="Cancelar"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 touch-manipulation flex-shrink-0"
                        >
                          <XIcon size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {member.name}
                            {isMe && <span className="ml-1.5 text-xs text-gray-400">(você)</span>}
                          </p>
                          <p className="text-xs text-gray-500">
                            {member.role === 'OWNER' ? L.settings.ownerLabel : L.settings.guestLabel}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 ml-3">
                          {isMe && (
                            <button
                              onClick={() => { setMyNameValue(member.name); setEditingMyName(true); }}
                              className="text-xs text-brand-600 font-medium touch-manipulation"
                            >
                              Editar
                            </button>
                          )}
                          {isOwner && !isMe && member.role !== 'OWNER' && (
                            <button
                              onClick={() => setRemoveTarget({ id: member.id, name: member.name })}
                              className="text-xs text-red-500 font-medium touch-manipulation"
                            >
                              {L.settings.removeButton}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Colaboradores — só para dono premium */}
        {isPremium && isOwner && household && (
          <div>
            <SectionHeader>{L.settings.collaboratorsSection}</SectionHeader>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <SettingsRow
                label={L.settings.manageCollab}
                value={L.settings.manageCollabDesc}
                action={{
                  label: L.settings.open,
                  onClick: () => navigate(`/collaborators/${household.id}`),
                  icon: <UserPlusIcon size={15} />,
                }}
              />
            </div>
          </div>
        )}

        {/* Compartilhar lista */}
        <div>
          <SectionHeader>{L.settings.shareSection}</SectionHeader>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {isPremium ? (
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-700">{L.settings.shareDesc}</p>
                {inviteUrl && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                    <p className="text-xs text-gray-500 break-all">{inviteUrl}</p>
                  </div>
                )}
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={handleGenerateInvite}
                  loading={generateInvite.isPending}
                  title={inviteUrl ? L.settings.newInvite : L.settings.generateInvite}
                  aria-label={inviteUrl ? L.settings.newInvite : L.settings.generateInvite}
                >
                  <ShareIcon size={16} />
                </Button>
                {inviteUrl && (
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent('Entre na minha lista do HomeStock: ' + inviteUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full h-12 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl text-sm transition-colors touch-manipulation"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {L.settings.shareWhatsapp}
                  </a>
                )}
              </div>
            ) : (
              <SettingsRow
                label={L.settings.shareUpgradeLabel}
                value={L.settings.shareUpgradeValue}
                action={{ label: L.settings.shareUpgradeButton, onClick: () => navigate('/settings/payment') }}
              />
            )}
          </div>
        </div>

        {/* Notificações */}
        <div>
          <SectionHeader>{L.settings.notificationsSection}</SectionHeader>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between py-3.5 px-4">
              <div>
                <p className="text-sm font-medium text-gray-900">{L.settings.inAppNotifs}</p>
                <p className="text-xs text-gray-500 mt-0.5">{L.settings.inAppNotifsDesc}</p>
              </div>
              <Toggle checked={pushNotifications} onChange={handleTogglePush} />
            </div>
          </div>
        </div>

        {/* Admin */}
        {isAdmin && (
          <div>
            <SectionHeader>{L.settings.adminSection}</SectionHeader>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <SettingsRow
                label={L.settings.adminPanel}
                action={{ label: L.settings.access, onClick: () => navigate('/admin') }}
              />
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleConfirmSignOut}
        title={L.settings.signOutTitle}
        message={L.settings.signOutMsg}
        confirmLabel={L.settings.signOutShort}
        confirmVariant="danger"
        loading={signingOut}
      />

      <ConfirmModal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleConfirmRemoveMember}
        title={L.settings.removeMemberTitle}
        message={removeTarget ? L.settings.removeMemberFrom(removeTarget.name) : ''}
        confirmLabel={L.settings.removeButton}
        confirmVariant="danger"
        loading={removeMember.isPending}
      />
    </div>
  );
}
